import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { isFullyWritten } from "@/lib/localizationPolicy";
import {
  BRAZIL_LOCATION_ID,
  TAX_NETWORK_BASE_URL,
  TRIBUTARIO_BASE_URL,
  isPTLocale,
} from '@/constants/branding';

export interface HreflangLink {
  lang: string;
  url: string;
}

/**
 * Generate hreflang links for a localized page
 * Uses localization table for routes and locations
 */
export const generateHreflangLinks = async (
  routeKey: string,
  nativeSlug?: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<HreflangLink[]> => {
  try {
    // Fetch route translations from localization_metadata table (source_type = 'route')
    const { data: routeTranslations } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug')
      .eq('source_type', 'route')
      .eq('source_id', routeKey);
    
    if (!routeTranslations) return [];
    
    // For location pages with multi-language slugs
    if (nativeSlug) {
      // First try locations table (English slugs)
      const { data: locationData } = await supabase
        .from('locations')
        .select('location_id, slug')
        .or(`slug.eq.${nativeSlug},native_slug.eq.${nativeSlug}`)
        .maybeSingle();

      let locationId = locationData?.location_id;
      let englishSlug = locationData?.slug;
      
      // If not found in locations, check localization_metadata table
      if (!locationId) {
        const { data: translation } = await supabase
          .from('localization_metadata')
          .select('source_id')
          .eq('source_type', 'location')
          .eq('translated_slug', nativeSlug)
          .maybeSingle();
        
        if (translation) {
          locationId = translation.source_id;
          // Get English slug from locations
          const { data: loc } = await supabase
            .from('locations')
            .select('slug')
            .eq('location_id', locationId)
            .single();
          englishSlug = loc?.slug;
        }
      }

      if (locationId) {
        // Get all non-English localized slugs from localization_metadata table
        const { data: locationTranslations } = await supabase
          .from('localization_metadata')
          .select('locale, translated_slug')
          .eq('source_type', 'location')
          .eq('source_id', locationId);

        const slugMap = new Map<string, string>();
        // Add English slug
        if (englishSlug) {
          slugMap.set('en-US', englishSlug);
        }
        // Add translations
        (locationTranslations || []).forEach(t => {
          if (t.translated_slug) slugMap.set(t.locale, t.translated_slug);
        });

        // Generate URLs with localized location slugs
        // STRICT: Only include locales with valid translations - no fallback to English
        return routeTranslations
          .filter(t => slugMap.has(t.locale) && t.translated_slug)
          .map(t => ({
            lang: t.locale,
            url: `${baseUrl}/${slugMap.get(t.locale)}/${t.translated_slug}`
          }));
      }
    }
    
    // For global pages
    return routeTranslations.map(t => ({
      lang: t.locale,
      url: `${baseUrl}/${t.translated_slug}`
    }));
  } catch (error) {
    logger.error('generateHreflangLinks failed', { routeKey, nativeSlug, error });
    return [];
  }
};

/**
 * Generate hreflang links for location homepage
 * Uses locations table for English, localization_metadata table for other locales
 * BRAND-AWARE: Brazil (ID 33) + PT locale → tributario.com.br/
 */
export const generateLocationHreflangLinks = async (
  locationSlug: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<HreflangLink[]> => {
  try {
    // First, try to find location by English slug in locations table
    let locationId: string | null = null;
    let englishSlug: string | null = null;

    const { data: location } = await supabase
      .from('locations')
      .select('location_id, slug')
      .or(`slug.eq.${locationSlug},native_slug.eq.${locationSlug}`)
      .maybeSingle();

    if (location) {
      locationId = String(location.location_id);
      englishSlug = location.slug;
    } else {
      // Fallback: check localization_metadata table for translated slug
      const { data: translation } = await supabase
        .from('localization_metadata')
        .select('source_id')
        .eq('source_type', 'location')
        .eq('translated_slug', locationSlug)
        .maybeSingle();

      if (translation) {
        locationId = translation.source_id;
        // Get English slug from locations
        const { data: loc } = await supabase
          .from('locations')
          .select('slug')
          .eq('location_id', locationId)
          .single();
        englishSlug = loc?.slug || null;
      }
    }

    if (!locationId) return [];

    // Check if this is Brazil location for brand-aware URL generation
    const isBrazil = locationId === BRAZIL_LOCATION_ID;

    // Build hreflang links
    const links: HreflangLink[] = [];

    // Add en-US using locations.slug (always tax.network for English)
    if (englishSlug) {
      links.push({ lang: 'en-US', url: `${TAX_NETWORK_BASE_URL}/${englishSlug}` });
    }

    // Get all non-English translations from localization_metadata table
    const { data: translations } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug')
      .eq('source_type', 'location')
      .eq('source_id', locationId);

    if (translations) {
      translations.forEach(t => {
        if (t.translated_slug) {
          // BRAND-AWARE: Brazil + PT locale → tributario.com.br/
          if (isBrazil && isPTLocale(t.locale)) {
            links.push({ lang: t.locale, url: `${TRIBUTARIO_BASE_URL}/` });
          } else {
            links.push({ lang: t.locale, url: `${TAX_NETWORK_BASE_URL}/${t.translated_slug}` });
          }
        }
      });
    }

    // Add x-default pointing to English version
    if (englishSlug) {
      links.push({ lang: 'x-default', url: `${TAX_NETWORK_BASE_URL}/${englishSlug}` });
    }

    return links;
  } catch (error) {
    logger.error('generateLocationHreflangLinks failed', { locationSlug, error });
    return [];
  }
};

/**
 * Get canonical URL for current page
 * Uses localization_metadata table for routes and locations
 */
export const getCanonicalUrl = async (
  routeKey: string,
  currentLanguage: string,
  nativeSlug?: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<string | null> => {
  try {
    // Get route translation for current language
    const { data: routeTrans } = await supabase
      .from('localization_metadata')
      .select('translated_slug')
      .eq('source_type', 'route')
      .eq('source_id', routeKey)
      .eq('locale', currentLanguage)
      .maybeSingle();
    
    // STRICT: No translation = no URL (return null, not fallback)
    const localizedPath = routeTrans?.translated_slug;
    if (!localizedPath) {
      // No route translation for this locale - return null to signal missing translation
      return null;
    }
    
    if (nativeSlug) {
      // locale-logic: Check against source locale constant for English detection
      // For English (source locale), get slug from locations table
      const isEnglish = currentLanguage === 'en-US' || currentLanguage === 'en' || currentLanguage === 'en-GB';
      if (isEnglish) {
        const { data: location } = await supabase
          .from('locations')
          .select('slug')
          .or(`slug.eq.${nativeSlug},native_slug.eq.${nativeSlug}`)
          .maybeSingle();

        if (location?.slug) {
          return `${baseUrl}/${location.slug}/${localizedPath}`;
        }
      }

      // For non-English, get from localization_metadata
      // First find the location_id
      let locationId: string | null = null;

      const { data: locData } = await supabase
        .from('locations')
        .select('location_id')
        .or(`slug.eq.${nativeSlug},native_slug.eq.${nativeSlug}`)
        .maybeSingle();

      if (locData) {
        locationId = locData.location_id;
      } else {
        const { data: translation } = await supabase
          .from('localization_metadata')
          .select('source_id')
          .eq('source_type', 'location')
          .eq('translated_slug', nativeSlug)
          .maybeSingle();
        locationId = translation?.source_id || null;
      }

      if (locationId) {
        const { data: langSlug } = await supabase
          .from('localization_metadata')
          .select('translated_slug')
          .eq('source_type', 'location')
          .eq('source_id', locationId)
          .eq('locale', currentLanguage)
          .maybeSingle();

        if (langSlug?.translated_slug) {
          return `${baseUrl}/${langSlug.translated_slug}/${localizedPath}`;
        }
      }

      // STRICT: No localized location slug = no canonical URL for this locale
      return null;
    }
    
    return `${baseUrl}/${localizedPath}`;
  } catch (error) {
    logger.error('getCanonicalUrl failed', { routeKey, currentLanguage, nativeSlug, error });
    // STRICT: Return null on error - no fallback to English
    return null;
  }
};

/**
 * Get canonical URL for location homepage
 * Uses locations table for English, localization_metadata table for other locales
 * BRAND-AWARE: Brazil (ID 33) + PT locale → tributario.com.br/
 */
export const getLocationCanonicalUrl = async (
  locationSlug: string,
  currentLanguage: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<string | null> => {
  try {
    // Find the location
    let locationId: string | null = null;
    let englishSlug: string | null = null;

    const { data: location } = await supabase
      .from('locations')
      .select('location_id, slug')
      .or(`slug.eq.${locationSlug},native_slug.eq.${locationSlug}`)
      .maybeSingle();

    if (location) {
      locationId = String(location.location_id);
      englishSlug = location.slug;
    } else {
      const { data: translation } = await supabase
        .from('localization_metadata')
        .select('source_id')
        .eq('source_type', 'location')
        .eq('translated_slug', locationSlug)
        .maybeSingle();
      
      if (translation) {
        locationId = translation.source_id;
        const { data: loc } = await supabase
          .from('locations')
          .select('slug')
          .eq('location_id', locationId)
          .single();
        englishSlug = loc?.slug || null;
      }
    }

    // STRICT: No location found = no URL
    if (!locationId) return null;

    // Check if this is Brazil location for brand-aware URL generation
    const isBrazil = locationId === BRAZIL_LOCATION_ID;

    // locale-logic: Check against source locale constant for English detection  
    // For English, return English slug from locations table (STRICT: require exact slug)
    const isEnglish = currentLanguage === 'en-US' || currentLanguage === 'en' || currentLanguage === 'en-GB';
    if (isEnglish) {
      return englishSlug ? `${TAX_NETWORK_BASE_URL}/${englishSlug}` : null;
    }

    // BRAND-AWARE: Brazil + PT locale → tributario.com.br/ (ROOT)
    if (isBrazil && isPTLocale(currentLanguage)) {
      return `${TRIBUTARIO_BASE_URL}/`;
    }

    // For non-English, get from localization_metadata table
    const { data: langSlug } = await supabase
      .from('localization_metadata')
      .select('translated_slug')
      .eq('source_type', 'location')
      .eq('source_id', locationId)
      .eq('locale', currentLanguage)
      .maybeSingle();

    // STRICT: No translation = no canonical URL (return null)
    const translatedSlug = langSlug?.translated_slug;
    if (!translatedSlug) {
      // No localized slug for this locale - return null to signal missing translation
      return null;
    }
    return `${TAX_NETWORK_BASE_URL}/${translatedSlug}`;
  } catch (error) {
    logger.error('getLocationCanonicalUrl failed', { locationSlug, currentLanguage, error });
    // STRICT: Return null on error - no fallback to English
    return null;
  }
};

/**
 * Generate hreflang links for publications with translated slugs
 * Uses localization_metadata table (source_type = 'publication')
 */
export const generatePublicationHreflangLinks = async (
  publicationId: string,
  vanityUrl: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<HreflangLink[]> => {
  try {
    // Fetch all translated slugs from localization_metadata table
    // NOTE: original_slug REMOVED (EN SOT refactor) - EN row's translated_slug is the source
    const { data: translations } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug, translated_name, translated_body_short')
      .eq('source_type', 'publication')
      .eq('source_id', publicationId);

    if (!translations || translations.length === 0) {
      return [];
    }

    // STRICT: Only emit URLs for locales with a valid slug AND a fully written
    // publication localization (title + excerpt). Partial rows are noindex, so
    // they must not be advertised as alternates or listed in the sitemap.
    return translations
      .filter(t => t.translated_slug && isFullyWritten('publication', t))
      .map(t => ({
        lang: t.locale,
        url: `${baseUrl}/${vanityUrl}/${t.translated_slug}`
      }));
  } catch (error) {
    logger.error('generatePublicationHreflangLinks failed', { publicationId, vanityUrl, error });
    return [];
  }
};

/**
 * Generate hreflang links for topic pages
 * Handles both global topics (/topics/slug) and location-specific topics (/location/topics/slug)
 */
export const generateTopicHreflangLinks = async (
  topicId: string,
  topicSlug: string,
  locationSlug?: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<HreflangLink[]> => {
  try {
    // Fetch topic slug translations from localization_metadata table
    const { data: topicTranslations } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug')
      .eq('source_type', 'topic')
      .eq('source_id', topicId);

    // Fetch 'topics' route translations
    const { data: routeTranslations } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug')
      .eq('source_type', 'route')
      .eq('source_id', 'topics');

    if (!topicTranslations || !routeTranslations) return [];

    const topicSlugMap = new Map(topicTranslations.map(t => [t.locale, t.translated_slug]));
    const routeSlugMap = new Map(routeTranslations.map(t => [t.locale, t.translated_slug]));

    // If location-specific, get location translations
    if (locationSlug) {
      // Find location_id
      let locationId: string | null = null;
      let englishLocationSlug: string | null = null;

      const { data: location } = await supabase
        .from('locations')
        .select('location_id, slug')
        .or(`slug.eq.${locationSlug},native_slug.eq.${locationSlug}`)
        .maybeSingle();

      if (location) {
        locationId = location.location_id;
        englishLocationSlug = location.slug;
      } else {
        const { data: translation } = await supabase
          .from('localization_metadata')
          .select('source_id')
          .eq('source_type', 'location')
          .eq('translated_slug', locationSlug)
          .maybeSingle();

        if (translation) {
          locationId = translation.source_id;
          const { data: loc } = await supabase
            .from('locations')
            .select('slug')
            .eq('location_id', locationId)
            .single();
          englishLocationSlug = loc?.slug || null;
        }
      }

      if (locationId) {
        // Get location translations from localization_metadata table
        const { data: locationTranslations } = await supabase
          .from('localization_metadata')
          .select('locale, translated_slug')
          .eq('source_type', 'location')
          .eq('source_id', locationId);

        const locationSlugMap = new Map<string, string>();
        if (englishLocationSlug) {
          locationSlugMap.set('en-US', englishLocationSlug);
        }
        (locationTranslations || []).forEach(t => {
          if (t.translated_slug) locationSlugMap.set(t.locale, t.translated_slug);
        });

        // Generate URLs: /{location}/{topics}/{topic-slug}
        // STRICT: Only include locales with valid translations for ALL segments
        return routeTranslations
          .filter(rt => 
            locationSlugMap.has(rt.locale) && 
            routeSlugMap.has(rt.locale) && 
            topicSlugMap.has(rt.locale)
          )
          .map(rt => ({
            lang: rt.locale,
            url: `${baseUrl}/${locationSlugMap.get(rt.locale)}/${routeSlugMap.get(rt.locale)}/${topicSlugMap.get(rt.locale)}`
          }));
      }
    }

    // Global topic: /{topics}/{topic-slug}
    // STRICT: Only include locales with valid translations
    return routeTranslations
      .filter(rt => routeSlugMap.has(rt.locale) && topicSlugMap.has(rt.locale))
      .map(rt => ({
        lang: rt.locale,
        url: `${baseUrl}/${routeSlugMap.get(rt.locale)}/${topicSlugMap.get(rt.locale)}`
      }));
  } catch (error) {
    logger.error('generateTopicHreflangLinks failed', { topicId, topicSlug, locationSlug, error });
    return [];
  }
};

/**
 * Generate hreflang links for topics listing page
 * Handles both global (/topics) and location-specific (/location/topics)
 */
export const generateTopicsListingHreflangLinks = async (
  locationSlug?: string,
  baseUrl: string = typeof window !== 'undefined' ? window.location.origin : ''
): Promise<HreflangLink[]> => {
  try {
    // Fetch 'topics' route translations
    const { data: routeTranslations } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug')
      .eq('source_type', 'route')
      .eq('source_id', 'topics');

    if (!routeTranslations) return [];

    const routeSlugMap = new Map(routeTranslations.map(t => [t.locale, t.translated_slug]));

    // If location-specific
    if (locationSlug) {
      let locationId: string | null = null;
      let englishLocationSlug: string | null = null;

      const { data: location } = await supabase
        .from('locations')
        .select('location_id, slug')
        .or(`slug.eq.${locationSlug},native_slug.eq.${locationSlug}`)
        .maybeSingle();

      if (location) {
        locationId = location.location_id;
        englishLocationSlug = location.slug;
      } else {
        const { data: translation } = await supabase
          .from('localization_metadata')
          .select('source_id')
          .eq('source_type', 'location')
          .eq('translated_slug', locationSlug)
          .maybeSingle();

        if (translation) {
          locationId = translation.source_id;
          const { data: loc } = await supabase
            .from('locations')
            .select('slug')
            .eq('location_id', locationId)
            .single();
          englishLocationSlug = loc?.slug || null;
        }
      }

      if (locationId) {
        const { data: locationTranslations } = await supabase
          .from('localization_metadata')
          .select('locale, translated_slug')
          .eq('source_type', 'location')
          .eq('source_id', locationId);

        const locationSlugMap = new Map<string, string>();
        if (englishLocationSlug) {
          locationSlugMap.set('en-US', englishLocationSlug);
        }
        (locationTranslations || []).forEach(t => {
          if (t.translated_slug) locationSlugMap.set(t.locale, t.translated_slug);
        });

        // STRICT: Only include locales with valid translations
        return routeTranslations
          .filter(rt => locationSlugMap.has(rt.locale) && routeSlugMap.has(rt.locale))
          .map(rt => ({
            lang: rt.locale,
            url: `${baseUrl}/${locationSlugMap.get(rt.locale)}/${routeSlugMap.get(rt.locale)}`
          }));
      }
    }

    // Global: /{topics}
    // STRICT: Only include locales with valid translations
    return routeTranslations
      .filter(rt => routeSlugMap.has(rt.locale))
      .map(rt => ({
        lang: rt.locale,
        url: `${baseUrl}/${routeSlugMap.get(rt.locale)}`
      }));
  } catch (error) {
    logger.error('generateTopicsListingHreflangLinks failed', { locationSlug, error });
    return [];
  }
};

/**
 * PROFILES: hreflang is identity-level, not translation-level.
 * Always emit all 36 primary locales, regardless of translation state.
 * All URLs are identical (canonical identity invariant).
 * Never queries localization_metadata -- locale list comes from system_locale_configs SOT.
 */
export function generateProfileHreflangLinks(
  canonicalUrl: string,
  primaryLocales: Array<{ locale: string }>
): HreflangLink[] {
  const links: HreflangLink[] = primaryLocales.map(l => ({
    lang: l.locale,
    url: canonicalUrl,
  }));
  links.push({ lang: 'x-default', url: canonicalUrl });
  return links;
}
